import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import {
  CloudUpload,
  List as ListIcon,
  LayoutGrid,
  FileText,
  SearchX,
  Layers,
} from "lucide-react";
import { useTranslation } from "@/packages/utils";
import {
  PageHeader,
  Filter,
  useFilter,
  StateCard,
  ConfirmDialog,
  Button,
} from "@/components/ui";
import type { ViewOption, SortOption, SortState } from "@/components/ui";
import { api } from "convex/_generated/api";
import {
  DocumentListView,
  DocumentGridView,
  UploadSheet,
  TagSelectorSheet,
} from "@/components/documents";
import { getDocumentFilterCategories } from "@/components/documents/document-filter-data";
import type { DocumentView } from "@/components/documents";
import {
  useDocuments,
  useDeleteDocument,
  useUpdateDocumentTags,
  useDocumentTags,
} from "@/hooks/use-documents";
import { useDocumentCategories } from "@/hooks/use-document-categories";
import { useDocumentFilters } from "@/hooks/use-document-filters";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { useChatContext } from "@/stores/chat-context-store";
import { convexClient } from "../../../lib/convex";
import type { Id } from "@/packages/types";

/* ------------------------------------------------------------------ */
/* Grouping state helpers                                              */
/* ------------------------------------------------------------------ */

const GROUPING_STORAGE_KEY = "documents-grouped";

function getStoredGrouped(): boolean {
  try {
    const stored = localStorage.getItem(GROUPING_STORAGE_KEY);
    return stored ? JSON.parse(stored) === true : true;
  } catch {
    return true;
  }
}

function getInitialGrouped(): boolean {
  return getStoredGrouped();
}

/* ------------------------------------------------------------------ */
/* View options                                                        */
/* ------------------------------------------------------------------ */

function getBaseViewOptions(
  t: (key: string) => string,
): Omit<ViewOption, "active">[] {
  return [
    { id: "list", label: t("documents:list.views.list"), icon: ListIcon },
    { id: "grid", label: t("documents:list.views.grid"), icon: LayoutGrid },
    {
      id: "upload",
      label: t("documents:list.views.upload"),
      icon: CloudUpload,
      variant: "cta",
    },
  ];
}

/* ------------------------------------------------------------------ */
/* Sort options                                                        */
/* ------------------------------------------------------------------ */

function getDocumentSortOptions(t: (key: string) => string): SortOption[] {
  return [
    { id: "uploadedAt", label: t("documents:list.sort.uploadDate") },
    { id: "fileName", label: t("documents:list.sort.name") },
    { id: "fileType", label: t("documents:list.sort.fileType") },
    { id: "status", label: t("documents:list.sort.status") },
  ];
}

const DEFAULT_DOCUMENT_SORT: SortState = {
  field: "uploadedAt",
  direction: "desc",
};

/* ------------------------------------------------------------------ */
/* DocumentsPage                                                       */
/* ------------------------------------------------------------------ */

export function DocumentsPage() {
  const { tags } = useDocumentTags();
  const { categories } = useDocumentCategories();
  const categoryGroups = useMemo(
    () => getDocumentFilterCategories(tags, categories),
    [tags, categories],
  );

  return (
    <Filter categoryGroups={categoryGroups}>
      <DocumentsPageContent />
    </Filter>
  );
}

function DocumentsPageContent() {
  const { t } = useTranslation(["documents", "common"]);
  const { workspaceSlug } = useParams({ strict: false });
  const { doc: docFromSearch } = useSearch({
    from: "/$workspaceSlug/documents",
  });
  const navigate = useNavigate();
  const [activeView, setActiveView] = useLocalStorage(
    "documents-active-view",
    "grid",
  );
  const [showUpload, setShowUpload] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [tagSelectorDocId, setTagSelectorDocId] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(searchValue);
  const [sortState, setSortState] = useState<SortState>(DEFAULT_DOCUMENT_SORT);
  const [grouped, setGrouped] = useState(getInitialGrouped);
  const { filters: activeFilters, clearAllFilters } = useFilter();
  const deleteDocument = useDeleteDocument();
  const updateDocumentTags = useUpdateDocumentTags();

  // Set ambient chat context for Documents page
  const setAmbientContext = useChatContext((s) => s.setAmbientContext);
  const clearAmbientContext = useChatContext((s) => s.clearAmbientContext);

  useEffect(() => {
    setAmbientContext({ page: "documents", label: t("documents:list.title") });
    return () => clearAmbientContext();
  }, [setAmbientContext, clearAmbientContext, t]);

  const filters = useDocumentFilters({
    search: debouncedSearch || undefined,
    sortBy: sortState.field,
    sortOrder: sortState.direction,
  });
  const { documents, isLoading } = useDocuments(filters);

  /* Derive view options with active state */
  const tFn = useCallback(
    (key: string) => t(key as "documents:list.views.list") as string,
    [t],
  );
  const baseViewOptions = useMemo(() => getBaseViewOptions(tFn), [tFn]);
  const viewOptions: ViewOption[] = baseViewOptions.map((opt) => ({
    ...opt,
    active: opt.id === activeView,
  }));
  const documentSortOptions = useMemo(() => getDocumentSortOptions(tFn), [tFn]);

  const filterSignature = useMemo(
    () => JSON.stringify(activeFilters),
    [activeFilters],
  );
  const previousSelectionContextRef = useRef<string>("");

  useEffect(() => {
    const nextSelectionContext = JSON.stringify({
      filterSignature,
      searchValue,
      sortField: sortState.field,
      sortDirection: sortState.direction,
    });

    if (
      previousSelectionContextRef.current &&
      previousSelectionContextRef.current !== nextSelectionContext
    ) {
      setSelectedIds(new Set());
    }

    previousSelectionContextRef.current = nextSelectionContext;
  }, [filterSignature, searchValue, sortState.field, sortState.direction]);

  const handleViewChange = (id: string) => {
    if (id === "upload") {
      setSelectedIds(new Set());
      setShowUpload(true);
      return;
    }

    setSelectedIds(new Set());
    setActiveView(id);
  };

  const handleSelectionChange = useCallback((nextSelectedIds: Set<string>) => {
    setSelectedIds(new Set(nextSelectedIds));
  }, []);

  const toggleGrouped = useCallback(() => {
    setGrouped((prev) => {
      const next = !prev;
      localStorage.setItem(GROUPING_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const handleTagClick = useCallback((docId: string) => {
    setTagSelectorDocId(docId);
    setShowTagSelector(true);
  }, []);

  const handleTagSelectorSave = useCallback(
    async (tagIds: string[]) => {
      if (!tagSelectorDocId) return;
      await updateDocumentTags({
        documentId: tagSelectorDocId as Id<"documents">,
        tagIds: tagIds as Id<"documentTags">[],
      });
    },
    [tagSelectorDocId, updateDocumentTags],
  );

  const handleBulkDownload = useCallback(async () => {
    const ids = Array.from(selectedIds) as Id<"documents">[];

    // Fetch all download URLs in parallel
    const results = await Promise.all(
      ids.map(async (id) => {
        const data = await convexClient.query(
          api.document_queries.getDownloadUrl,
          { id },
        );
        return data?.url && data.fileName
          ? { url: data.url, fileName: data.fileName }
          : null;
      }),
    );

    // Download each file by fetching as blob (cross-origin URLs ignore
    // the `download` attribute, so we must create object URLs)
    for (const result of results) {
      if (!result) continue;
      try {
        const response = await fetch(result.url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = result.fileName;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        await new Promise((r) => setTimeout(r, 150));
      } catch {
        // Fallback: open in new tab if blob fetch fails
        window.open(result.url, "_blank");
      }
    }
  }, [selectedIds]);

  const handleSingleDownload = useCallback(async (docId: string) => {
    try {
      const data = await convexClient.query(
        api.document_queries.getDownloadUrl,
        { id: docId as Id<"documents"> },
      );

      if (!data?.url || !data.fileName) {
        console.error("No download URL available");
        return;
      }

      const response = await fetch(data.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = data.fileName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  }, []);

  const handleSingleDelete = useCallback(async (docId: string) => {
    // Set single document as selected
    setSelectedIds(new Set([docId]));
    // Show confirmation dialog
    setShowBulkDeleteConfirm(true);
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    setShowBulkDeleteConfirm(true);
  }, [selectedIds]);

  const confirmBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds) as Id<"documents">[];
    setIsBulkDeleting(true);
    try {
      await Promise.all(ids.map((id) => deleteDocument({ id })));
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedIds, deleteDocument]);

  // Sync URL search param -> navigate to document route for external links
  useEffect(() => {
    if (docFromSearch) {
      setShowUpload(false);
      navigate({
        to: "/$workspaceSlug/documents/$documentId",
        params: {
          workspaceSlug: workspaceSlug ?? "",
          documentId: docFromSearch,
        },
      });
    }
  }, [docFromSearch, navigate, workspaceSlug]);

  const openDetail = (id: string) => {
    setShowUpload(false);
    navigate({
      to: "/$workspaceSlug/documents/$documentId",
      params: { workspaceSlug: workspaceSlug ?? "", documentId: id },
    });
  };

  /* Group documents by category for list view */
  const groupedDocuments = useMemo(() => {
    const groups = new Map<string, DocumentView[]>();
    if (!documents) return groups;
    for (const doc of documents) {
      // Format: "iconName|categoryName" for proper icon rendering in list view
      const categoryKey =
        doc.categoryIcon && doc.categoryName
          ? `${doc.categoryIcon}|${doc.categoryName}`
          : "folder|Uncategorized";
      if (!groups.has(categoryKey)) {
        groups.set(categoryKey, []);
      }
      groups.get(categoryKey)?.push(doc);
    }
    return groups;
  }, [documents]);

  const isEmpty = !isLoading && documents && documents.length === 0;
  const hasActiveFilters = activeFilters.length > 0 || searchValue.length > 0;

  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    setSearchValue("");
    setSelectedIds(new Set());
  }, [clearAllFilters]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title={t("documents:list.title")}
        viewOptions={viewOptions}
        onViewChange={handleViewChange}
        filterSlot={
          <>
            <Button
              variant={grouped ? "accent-soft" : "ghost"}
              size="sm"
              icon={Layers}
              className="rounded-[6px] px-2.5"
              onClick={toggleGrouped}
            >
              {t("documents:list.group")}
            </Button>
            <Filter.Popover />
          </>
        }
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t("documents:list.searchPlaceholder")}
        sortOptions={documentSortOptions}
        activeSort={sortState}
        onSortChange={setSortState}
        selectedCount={selectedIds.size}
        onBulkDownload={selectedIds.size > 0 ? handleBulkDownload : undefined}
        onBulkDelete={selectedIds.size > 0 ? handleBulkDelete : undefined}
      />

      <Filter.PillBar />

      <div className="flex-1 overflow-hidden">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <StateCard variant="loading">
              <StateCard.Icon />
              <StateCard.Title>{t("documents:list.loading")}</StateCard.Title>
              <StateCard.Description>
                {t("documents:list.loadingDescription")}
              </StateCard.Description>
            </StateCard>
          </div>
        )}

        {/* Empty state — no results matching filters */}
        {isEmpty && hasActiveFilters && (
          <div className="flex items-center justify-center h-full">
            <StateCard variant="empty">
              <StateCard.Icon />
              <StateCard.Title>{t("documents:list.noMatch")}</StateCard.Title>
              <StateCard.Description>
                {t("documents:list.noMatchDescription")}
              </StateCard.Description>
              <StateCard.Action>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors cursor-pointer"
                >
                  <SearchX size={14} />
                  {t("documents:list.clearFilters")}
                </button>
              </StateCard.Action>
            </StateCard>
          </div>
        )}

        {/* Empty state — truly no data */}
        {isEmpty && !hasActiveFilters && (
          <div className="flex items-center justify-center h-full">
            <StateCard variant="empty">
              <StateCard.Icon />
              <StateCard.Title>{t("documents:list.empty")}</StateCard.Title>
              <StateCard.Description>
                {t("documents:list.emptyDescription")}
              </StateCard.Description>
              <StateCard.Action>
                <button
                  type="button"
                  onClick={() => setShowUpload(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors cursor-pointer"
                >
                  <FileText size={14} />
                  {t("documents:list.uploadDocument")}
                </button>
              </StateCard.Action>
            </StateCard>
          </div>
        )}

        {/* Document views */}
        {!isLoading && !isEmpty && (
          <AnimatePresence mode="wait">
            {activeView === "list" && (
              <DocumentListView
                key="list"
                groupedDocuments={grouped ? groupedDocuments : null}
                documents={grouped ? undefined : (documents ?? [])}
                onRowClick={openDetail}
                onSelectionChange={handleSelectionChange}
                onTagClick={handleTagClick}
                onDownload={handleSingleDownload}
                onDelete={handleSingleDelete}
              />
            )}

            {activeView === "grid" && (
              <DocumentGridView
                key="grid"
                groupedDocuments={grouped ? groupedDocuments : null}
                documents={grouped ? undefined : (documents ?? [])}
                onOpen={openDetail}
                onTagClick={handleTagClick}
              />
            )}
          </AnimatePresence>
        )}
      </div>

      <UploadSheet
        open={showUpload}
        onClose={() => setShowUpload(false)}
        recentDocuments={documents}
      />

      <TagSelectorSheet
        open={showTagSelector}
        onClose={() => {
          setShowTagSelector(false);
          setTagSelectorDocId(null);
        }}
        documentTags={documents?.find((d) => d.id === tagSelectorDocId)?.tags}
        documentName={documents?.find((d) => d.id === tagSelectorDocId)?.title}
        onSave={handleTagSelectorSave}
      />

      <ConfirmDialog
        open={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
      >
        <ConfirmDialog.Title>
          {t("documents:bulkDelete.title")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p className="text-[13px] text-secondary">
            {t(
              selectedIds.size === 1
                ? "documents:bulkDelete.description"
                : "documents:bulkDelete.description_plural",
              { count: selectedIds.size },
            )}
          </p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={confirmBulkDelete}
          onCancel={() => setShowBulkDeleteConfirm(false)}
          confirmLabel={t("documents:bulkDelete.confirm")}
          cancelLabel={t("common:buttons.cancel")}
          variant="danger"
          loading={isBulkDeleting}
        />
      </ConfirmDialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Route export                                                        */
/* ------------------------------------------------------------------ */

// The actual content is rendered by the layout route (route.tsx) so that it
// stays mounted when navigating to /documents/$documentId.  The index route
// is intentionally empty — it exists only so TanStack Router has a leaf to match.
export const Route = createFileRoute("/$workspaceSlug/documents/")({
  component: () => null,
});
